/**
 * useFormSnapshot — F-Refinement-10 / Wave 2.5
 *
 * Encapsulates the per-field useState + snapshot baseline + seed-on-open
 * useEffect pattern used across ROROS form sheets (AiOperatorEditor,
 * ActorFormDialog, ReadinessAssessmentDialog, GoalFormDialog, OutcomeFormDialog).
 *
 * The hook manages:
 *   - Entire form state as a single object (T).
 *   - A `baseline` snapshot captured on open (used by FormSheet for dirty detection).
 *   - Re-seeding when the sheet opens or when `depKey` changes (e.g. a different
 *     item is being edited).
 *
 * `diff` is NOT part of this hook — diff semantics vary per consumer and are
 * passed directly to FormSheet.
 *
 * Typical usage:
 *
 *   const { state, setField, baseline, current, setBaseline, reset } =
 *     useFormSnapshot({
 *       initialFactory: () => ({
 *         title: editingGoal?.title ?? '',
 *         domainId: editingGoal?.domainId ?? strategyDomainId,
 *         // ... other fields
 *       }),
 *       isOpen,
 *       depKey: editingGoal?.id ?? null,
 *     });
 *
 *   // Use state.title, setField('title', v), etc. in JSX.
 *   // Pass baseline and current (=state) to FormSheet.
 *
 * API:
 *   state       — current form state object
 *   setField    — update a single field immutably
 *   baseline    — snapshot captured on open (for dirty detection)
 *   current     — alias for state (FormSheet API convenience)
 *   setBaseline — replace baseline entirely (post-save bypass) OR pass a
 *                 functional updater (next: T | (prev: T) => T) for partial
 *                 patches such as async data loads
 *   reset       — re-runs initialFactory and replaces both state and baseline
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export function useFormSnapshot<T extends object>(args: {
  /** Called to produce the initial / reset form state. Captured via ref so it
   *  always uses the latest closure values without being a useEffect dep. */
  initialFactory: () => T;
  /** Whether the sheet/dialog is open. Seeds on false→true transition. */
  isOpen: boolean;
  /** Optional key that triggers a re-seed when it changes while isOpen is true
   *  (e.g. the ID of the item being edited). Use null/undefined for create mode. */
  depKey?: string | number | null;
}): {
  state: T;
  setField: <K extends keyof T>(key: K, value: T[K]) => void;
  baseline: T;
  /** Alias for `state` — FormSheet convenience. */
  current: T;
  /**
   * Replace the baseline entirely (post-save bypass to clear dirty state) OR
   * pass a functional updater `(prev: T) => T` for partial async patches.
   */
  setBaseline: (next: T | ((prev: T) => T)) => void;
  /** Re-runs initialFactory and replaces both state and baseline. */
  reset: () => void;
} {
  const { initialFactory, isOpen, depKey } = args;

  // Always point to the latest factory without adding it to effect deps.
  const factoryRef = useRef(initialFactory);
  factoryRef.current = initialFactory;

  const [state, setStateInternal] = useState<T>(() => initialFactory());
  const [baseline, setBaselineInternal] = useState<T>(() => initialFactory());

  // Seed state and baseline whenever the sheet opens or the edited item changes.
  useEffect(() => {
    if (!isOpen) return;
    const initial = factoryRef.current();
    setStateInternal(initial);
    setBaselineInternal(initial);
  // depKey intentionally included; initialFactory excluded (tracked via ref).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, depKey]);

  const setField = useCallback(<K extends keyof T>(key: K, value: T[K]) => {
    setStateInternal((prev) => ({ ...prev, [key]: value }) as T);
  }, []);

  /** Accepts a full replacement T or a functional updater (prev: T) => T. */
  const setBaseline = useCallback((next: T | ((prev: T) => T)) => {
    if (typeof next === 'function') {
      setBaselineInternal(next as (prev: T) => T);
    } else {
      setBaselineInternal(next);
    }
  }, []);

  const reset = useCallback(() => {
    const initial = factoryRef.current();
    setStateInternal(initial);
    setBaselineInternal(initial);
  }, []);

  return {
    state,
    setField,
    baseline,
    current: state,
    setBaseline,
    reset,
  };
}
